import { Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import ScrollTable from 'components/global/CUK/ScrollTable'
import TableSkeleton from 'components/global/CUK/Skeletons/TableSkeleton'
import TableView, { IItem, useTable } from 'packages/ui/Table'

import { useSelection, useSort } from '../store'
import { UseSitesUnscope } from '../use-sites-unscope'
import COLUMNS_CONFIG, { getItemIds } from './config-columns'

type TableProps = {
  apiSites: UseSitesUnscope
}

const Table = ({ apiSites }: TableProps) => {
  const { t } = useTranslation()
  const [sort, toggleSort] = useSort()
  const navigate = useNavigate()
  const { hasSelection, isSelected, toggle } = useSelection()
  const items = apiSites.sites as unknown as IItem[]

  const handleRowClick = (item: IItem) => {
    const id = String(item.id)
    if (hasSelection) {
      toggle(id, getItemIds(items))
      return
    }
    navigate(id)
  }

  const apiTable = useTable({
    rowKey: 'id',
    loading: apiSites.loading,
    loadingNext: apiSites.loadingNext,
    items,
    columns: COLUMNS_CONFIG,
    onRowClick: handleRowClick,
    rowIsBold: item => isSelected(String(item.id))
  })

  const handleSortChange = (columnName: string) => {
    const column = COLUMNS_CONFIG.find(({ name }) => name === columnName)
    toggleSort(column?.sortBy ?? columnName)
  }

  return (
    <Stack
      sx={{
        overflow: 'hidden',
        overflowY: 'auto',
        flex: '1 1 auto',
        scrollbarWidth: 'none',
        height: '100%',
        '&::-webkit-scrollbar ': {
          display: 'none'
        }
      }}>
      <ScrollTable
        fetchNextPage={apiSites.fetchNextPage}
        shouldFetchNextPage={apiSites.shouldFetchNextPage}
      >
        {apiSites.loading ? (
          <TableSkeleton />
        ) : (
          <TableView
            apiTable={apiTable}
            noItemsMessage={t('admin_sites.table.empty')}
            sort={{ ...sort, onChange: handleSortChange }}
          />
        )}
      </ScrollTable>
    </Stack>
  )
}

export default Table
